/**
 * Prepared drops actions: bulk send, duplicate, copy as draft.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useToast } from '@/providers/ToastProvider';
import { PREPARED_DROPS_UNDO_KEY } from '@/features/prepared-drops-undo';
import { bulkSendPreparedDropsStream, bulkCreateSmswiserDraftsStream } from '@/hooks';
import { groupDraftIdsForBulkSend } from '@/features/smswiser-campaigns/utils/preparedDropBulkByCountry';
import { getApiBaseUrl, getAuthToken } from '@/services/api';
import type { PreparedDrop } from '@/features/smswiser-campaigns/types/workspace.types';
import { buildCopiedPreparedDropPayload } from '@/features/smswiser-campaigns/utils/buildCopiedPreparedDropPayload';
import { formatDateDMY } from '@/features/smswiser-campaigns/utils/dateFormat';
import { getApiErrorMessage } from '@/lib/utils';

import type { PrepareDraftsPayload } from '@/features/smswiser-campaigns/components/PrepareNewDropsForm/shared/PrepareNewDropsForm.types';
import { buildCreateDraftItems } from '../utils/buildCreateDraftItems';

import { useSentDropActions } from './useSentDropActions';
import { runPreparedCreates, type PreparedCreateDeps } from './runPreparedCreates';

const noop = () => undefined;

export interface UsePreparedDropsActionsParams {
  countryKey: string;
  drops: PreparedDrop[];
  prepared: { refetch: () => void };
  onSentSuccess: () => void;
  createPreparedFlex: PreparedCreateDeps['createPreparedFlex'];
  /** When provided, duplicate / copy-as-draft use the one-request bulk endpoint. */
  bulkCreatePrepared?: PreparedCreateDeps['bulkCreatePrepared'];
}

export function usePreparedDropsActions({
  countryKey,
  drops,
  prepared,
  onSentSuccess,
  createPreparedFlex,
  bulkCreatePrepared,
}: UsePreparedDropsActionsParams) {
  const { success: toastSuccess, error: toastError, loading: toastLoading, update: toastUpdate, dismiss: toastDismiss } = useToast();
  const sentActions = useSentDropActions({ countryKey, prepared, drops });
  const qc = useQueryClient();
  // Bulk send and staging stream over raw fetch, not a mutation, so nothing
  // else refreshes the undo status — without this the button stays hidden
  // until the next 60s poll, exactly when it is wanted most.
  const revealUndo = useCallback(() => {
    void qc.invalidateQueries({ queryKey: PREPARED_DROPS_UNDO_KEY });
  }, [qc]);

  const handleBulkSend = useCallback(
    async (
      selectedIds: string[],
      onProgress?: (sent: number, failed: number) => void,
    ): Promise<{ sent: number; failed: number; errorMessages?: string[] }> => {
      const draftDrops = drops.filter((d) => d.status === 'DRAFT' && selectedIds.includes(d.id));
      const groups = groupDraftIdsForBulkSend(draftDrops);
      let totalSent = 0;
      let totalFailed = 0;
      const errorMessages: string[] = [];
      for (const { countryKey: ck, wiserId: wid, ids } of groups) {
        await bulkSendPreparedDropsStream(ck, wid, ids, {
          getAuthToken,
          getApiBase: getApiBaseUrl,
          onEvent: (event) => {
            if (event.status === 'sent') {
              totalSent += 1;
              onProgress?.(totalSent, totalFailed);
            } else if (event.status === 'error') {
              totalFailed += 1;
              if (event.message) errorMessages.push(event.message);
              onProgress?.(totalSent, totalFailed);
            } else if (event.status === 'done') {
              // Counts already accumulated from sent/error events
            }
          },
        });
      }
      prepared.refetch();
      revealUndo();
      if (totalSent > 0) onSentSuccess();
      return { sent: totalSent, failed: totalFailed, errorMessages };
    },
    [drops, onSentSuccess, prepared, revealUndo],
  );

  const handleCreateSmswiserDraft = useCallback(
    async (selectedIds: string[]): Promise<void> => {
      const draftDrops = drops.filter((d) => d.status === 'DRAFT' && selectedIds.includes(d.id));
      if (draftDrops.length === 0) return;
      const groups = groupDraftIdsForBulkSend(draftDrops);
      const loadingId = toastLoading('Creating SMSWiser drafts…', `0 of ${draftDrops.length}`);
      let created = 0;
      let failed = 0;
      const errors: string[] = [];
      try {
        for (const { countryKey: ck, wiserId: wid, ids } of groups) {
          await bulkCreateSmswiserDraftsStream(ck, wid, ids, {
            getAuthToken,
            getApiBase: getApiBaseUrl,
            onEvent: (event) => {
              if (event.status === 'created') created += 1;
              else if (event.status === 'error') {
                failed += 1;
                if (event.message) errors.push(event.message);
              } else return;
              toastUpdate(loadingId, 'Creating SMSWiser drafts…', `${created + failed} of ${draftDrops.length}`);
            },
          });
        }
        toastDismiss(loadingId);
        prepared.refetch();
        revealUndo();
        if (failed > 0) {
          toastError('Some drafts failed', `${created} created, ${failed} failed. ${errors[0] ?? ''}`);
        } else {
          toastSuccess('SMSWiser drafts created', `Staged ${created} draft${created !== 1 ? 's' : ''} on SMSWiser.`);
        }
      } catch (e) {
        toastDismiss(loadingId);
        toastError('Create draft failed', getApiErrorMessage(e, 'Could not create SMSWiser drafts.'));
        throw e;
      }
    },
    [drops, prepared, revealUndo, toastLoading, toastUpdate, toastDismiss, toastSuccess, toastError],
  );

  const handleDuplicateDraft = useCallback(
    async (draftDrops: PreparedDrop[], copiesPerDraft: number, targetWorkDate: string) => {
      const n = Math.max(1, Math.min(100, Math.floor(copiesPerDraft) || 1));
      const total = draftDrops.length * n;
      const loadingId = toastLoading('Duplicating drafts...', `0 of ${total} created`);
      try {
        const items = draftDrops.flatMap((d) =>
          Array.from({ length: n }, () => ({
            countryKey: d.countryKey,
            wiserId: d.wiserId,
            payload: buildCopiedPreparedDropPayload(d, targetWorkDate, 'duplicate'),
          })),
        );
        await runPreparedCreates(items, { bulkCreatePrepared, createPreparedFlex }, (done) =>
          toastUpdate(loadingId, 'Duplicating drafts...', `${done} of ${total} created`),
        );
        toastDismiss(loadingId);
        const dateFormatted = formatDateDMY(targetWorkDate);
        toastSuccess('Duplicated', `Created ${total} new draft${total !== 1 ? 's' : ''} for ${dateFormatted}.`);
      } catch (e) {
        toastDismiss(loadingId);
        toastError('Duplicate failed', getApiErrorMessage(e, 'Could not create drafts.'));
        throw e;
      }
    },
    [createPreparedFlex, bulkCreatePrepared, toastSuccess, toastError, toastLoading, toastUpdate, toastDismiss],
  );

  const handleCopyAsDraft = useCallback(
    async (sentDrops: PreparedDrop[], targetWorkDate: string) => {
      const total = sentDrops.length;
      const loadingId = toastLoading('Copying as drafts...', `0 of ${total} copied`);
      try {
        const items = sentDrops.map((d) => ({
          countryKey: d.countryKey,
          wiserId: d.wiserId,
          payload: buildCopiedPreparedDropPayload(d, targetWorkDate, 'copy-as-draft'),
        }));
        await runPreparedCreates(items, { bulkCreatePrepared, createPreparedFlex }, (done) =>
          toastUpdate(loadingId, 'Copying as drafts...', `${done} of ${total} copied`),
        );
        toastDismiss(loadingId);
        toastSuccess('Copied as drafts', `${total} drop${total !== 1 ? 's' : ''} copied to ${formatDateDMY(targetWorkDate)}.`);
      } catch (e) {
        toastDismiss(loadingId);
        toastError('Copy as draft failed', getApiErrorMessage(e, 'Could not create drafts.'));
        throw e;
      }
    },
    [createPreparedFlex, bulkCreatePrepared, toastLoading, toastUpdate, toastDismiss, toastSuccess, toastError],
  );

  const handleReinstate = useCallback(
    async (sourceDrops: PreparedDrop[], targetWorkDate: string) => {
      const total = sourceDrops.length;
      if (total === 0) return;
      const loadingId = toastLoading('Reinstating drops...', `0 of ${total} created`);
      try {
        const items = sourceDrops.map((d) => ({
          countryKey: d.countryKey,
          wiserId: d.wiserId,
          payload: buildCopiedPreparedDropPayload(d, targetWorkDate, 'copy-as-draft'),
        }));
        await runPreparedCreates(items, { bulkCreatePrepared, createPreparedFlex }, (done) =>
          toastUpdate(loadingId, 'Reinstating drops...', `${done} of ${total} created`),
        );
        toastDismiss(loadingId);
        toastSuccess(
          'Reinstated',
          `Created ${total} draft${total !== 1 ? 's' : ''} for ${formatDateDMY(targetWorkDate)}.`,
        );
      } catch (e) {
        toastDismiss(loadingId);
        toastError('Reinstate failed', getApiErrorMessage(e, 'Could not create drafts.'));
        throw e;
      }
    },
    [createPreparedFlex, bulkCreatePrepared, toastLoading, toastUpdate, toastDismiss, toastSuccess, toastError],
  );

  const handleCreateDrafts = useCallback(
    async (
      payload: PrepareDraftsPayload,
      ctx: { wiserId: number; workDate: string },
    ): Promise<void> => {
      const items = buildCreateDraftItems(payload, { countryKey, ...ctx });
      await runPreparedCreates(
        items,
        { bulkCreatePrepared, createPreparedFlex },
        noop,
      );
    },
    [countryKey, bulkCreatePrepared, createPreparedFlex],
  );

  return {
    ...sentActions,
    handleBulkSend,
    handleCreateSmswiserDraft,
    handleDuplicateDraft,
    handleCopyAsDraft,
    handleReinstate,
    handleCreateDrafts,
  };
}
